import { useGT } from 'gt-react';
import type { FeedbackKind } from '@erp/shared-types';
import { FEEDBACK_KINDS } from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import { useFeedbackKindLabel } from './useFeedbackLabels';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function FeedbackKindBadge({ kind }: { kind: string }) {
  const label = useFeedbackKindLabel();
  return <Badge variant="outline">{label(kind)}</Badge>;
}

const ALL = '__all__';

/**
 * Single-select of feedback kinds. With `allowAll` the first option is
 * "All types" and maps to `undefined`, for use as a filter.
 */
export function FeedbackKindSelect({
  value,
  onChange,
  allowAll,
  disabled,
  triggerId,
  className,
}: {
  value: FeedbackKind | undefined;
  onChange: (kind: FeedbackKind | undefined) => void;
  allowAll?: boolean;
  disabled?: boolean;
  triggerId?: string;
  className?: string;
}) {
  const gt = useGT();
  const label = useFeedbackKindLabel();
  return (
    <Select
      value={value ?? (allowAll ? ALL : undefined)}
      onValueChange={(next) => {
        if (!next) return;
        onChange(next === ALL ? undefined : (next as FeedbackKind));
      }}
      disabled={disabled}
    >
      <SelectTrigger id={triggerId} className={className}>
        <SelectValue
          placeholder={gt('Select a type', {
            context: 'placeholder for the feedback kind select',
          })}
        />
      </SelectTrigger>
      <SelectContent>
        {allowAll && (
          <SelectItem value={ALL}>
            {gt('All types', { context: 'feedback kind filter: no filter' })}
          </SelectItem>
        )}
        {FEEDBACK_KINDS.map((kind) => (
          <SelectItem key={kind} value={kind}>
            {label(kind)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
